import { AG_GRID_ICON_SETUP } from 'constantsProject';
import { usePageFilterSaver } from 'hooks/usePageFilterSaver';
import { Loader } from 'uikit';

import type { BodyScrollEndEvent, FilterChangedEvent, GridApi, GridReadyEvent } from 'ag-grid-community';
import type { AgGridReactProps } from 'ag-grid-react';
import { AgGridReact } from 'ag-grid-react';
import cn from 'classnames';
import type { ForwardedRef, MutableRefObject } from 'react';
import { useRef, useEffect, forwardRef, useCallback, useMemo } from 'react';

import { defaultColDef, getColumnsWithTooltip, getFilterActiveIcon } from './helpers';

import Styles from './agGridTable.module.css';

type TAgGridTable<T> = Omit<AgGridReactProps<T>, 'context'> & {
    isWithoutColumnsTooltip?: boolean;
    context?: {
        tableUniqueId?: string;
        [key: string]: any;
    };
    isContainerHeight?: boolean;
};

const TableLoadingComponent = () => <Loader />;

const AgGridTableRenderFunc = <TData = any,>(props: TAgGridTable<TData>, ref: ForwardedRef<AgGridReact>) => {
    const {
        isWithoutColumnsTooltip = false,
        columnDefs,
        onGridReady,
        context,
        onFilterChanged,
        defaultColDef: defaultColDefProps,
        isContainerHeight,
        ...rest
    } = props;

    const tableWrapperRef = useRef<HTMLDivElement>(null);

    const columns = useMemo(() => {
        if (isWithoutColumnsTooltip) {
            return columnDefs;
        }

        return getColumnsWithTooltip(columnDefs);
    }, [columnDefs, isWithoutColumnsTooltip]);

    const { savedFilterState } = usePageFilterSaver({});

    const setActiveFilterIcons = useCallback(
        ({ columnApi }: { columnApi: GridApi | undefined }) => {
            const savedFilterValues = !!context?.tableUniqueId && savedFilterState?.[context?.tableUniqueId];

            const tableColumns = columnApi?.getAllGridColumns?.();

            tableColumns?.forEach((column) => {
                const currentId = column.getId();
                const fieldValue = savedFilterValues?.[currentId];

                setTimeout(() => {
                    getFilterActiveIcon(currentId, fieldValue?.length, tableWrapperRef);
                }, 500);
            });
        },
        [context, savedFilterState],
    );

    useEffect(() => {
        const { current } = (ref ?? {}) as MutableRefObject<AgGridReact | null>;

        const scrollFilterIconShow = (sc: BodyScrollEndEvent) => {
            setActiveFilterIcons({
                columnApi: sc?.api,
            });
        };

        if (current?.api?.addEventListener) {
            current?.api?.addEventListener('bodyScrollEnd', scrollFilterIconShow);
        }

        return () => {
            if (current?.api?.removeEventListener) {
                current?.api?.removeEventListener('bodyScrollEnd', scrollFilterIconShow);
            }
        };
    }, [ref, setActiveFilterIcons]);

    useEffect(() => {
        const { current } = (ref ?? {}) as MutableRefObject<AgGridReact | null>;

        setActiveFilterIcons({
            columnApi: current?.api,
        });
    }, [context?.tableUniqueId, ref, setActiveFilterIcons]);

    const onReady = useCallback(
        (event: GridReadyEvent<TData, any>) => {
            onGridReady?.(event);

            event.api.refreshHeader();
            setActiveFilterIcons({
                columnApi: event.api,
            });
        },
        [onGridReady, setActiveFilterIcons],
    );

    const onFilterChangedHandler = useCallback(
        (event: FilterChangedEvent) => {
            const { fieldName, checkedValues } = event as any;

            onFilterChanged?.(event);
            getFilterActiveIcon(fieldName, checkedValues?.length, tableWrapperRef);
        },
        [onFilterChanged],
    );

    const defaultColDefMemo = useMemo(() => ({ ...defaultColDef, ...defaultColDefProps }), [defaultColDefProps]);

    return (
        <div
            className={cn(
                'ag-theme-alpine',
                Styles.agGridTable,
                isContainerHeight ? Styles.agGridTableContainerHeight : undefined,
            )}
            ref={tableWrapperRef}
        >
            <AgGridReact
                ref={ref}
                columnDefs={columns}
                defaultColDef={defaultColDefMemo}
                tooltipShowDelay={400}
                icons={AG_GRID_ICON_SETUP}
                suppressDragLeaveHidesColumns
                context={context}
                onGridReady={onReady}
                onFilterChanged={onFilterChangedHandler}
                overlayNoRowsTemplate="Нет данных"
                loadingOverlayComponent={TableLoadingComponent}
                {...rest}
            />
        </div>
    );
};

export const AgGridTable = forwardRef(AgGridTableRenderFunc) as <T>(
    props: TAgGridTable<T> & {
        ref?: ForwardedRef<AgGridReact>;
    },
) => ReturnType<typeof AgGridTableRenderFunc>;

AgGridTableRenderFunc.displayName = 'AgGridTable';
TableLoadingComponent.displayName = 'TableLoadingComponent';
